import { useState, useEffect, useRef } from "react";

// Drop-in replacement for useState that syncs the value to localStorage
// under a stable key. Used for collapse/chevron states across the app
// (sidebar Datasets section, per-folder open state, Settings panels,
// Investigation panel, Party Audit panel, etc.) so users don't have to
// re-open the same sections every time they navigate away and back.
//
// Reads the initial value from localStorage on first mount; falls back
// to `initial` if nothing stored or JSON parse fails. Writes happen on
// every state change through an effect so React state is the source of
// truth and storage is a mirror.
export function usePersistedState(key, initial) {
  const isFirstRender = useRef(true);
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initial;
      return JSON.parse(raw);
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota exceeded / private mode — silently ignore */
    }
  }, [key, value]);

  return [value, setValue];
}
